import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useExpenseBoard, useExpenseQueue, useReimburse, type ExpenseBoard } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

const BUCKET_CLS: Record<string, string> = {
  "0-30": "text-brand-charcoal", "31-60": "text-brand-primary",
  "61-90": "text-status-amber", "90+": "text-status-red",
};

/** What the company owes its own people, and for how long (HRX-05).
 *
 * The front half of expenses works — claims are filed, approved and sanctioned, with an approver on every one.
 * The back half had never run: not a single Payment Entry against an Employee existed on the site, the ledger
 * showed 7.95 M CDF credited to employees and nothing debited, and ten claims were over ninety days old. The
 * oldest was a project manager out of pocket 720,000 CDF for 189 days, which is why the oldest row is named
 * here rather than folded into a total. */
export default function Owed({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const board = useExpenseBoard(company || undefined);
  const queue = useExpenseQueue(company || undefined);
  const pay = useReimburse();

  if (board.isError) return <div className="mt-6"><ErrorBox error={board.error} onRetry={() => void board.refetch()} /></div>;
  if (!board.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = board.data;

  return (
    <div className="mt-4 space-y-4" data-expenses-owed>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-expenses-strip>
        <Stat label={t("exp.owed")} value={<Money value={d.owed.amount} lng={lng} />}
          cls={d.owed.amount ? "text-status-amber" : "text-status-green"}
          hint={t("exp.owedHint", { n: d.owed.claims })} />
        <Stat label={t("exp.oldest")} value={d.owed.oldest ? t("exp.daysN", { n: d.owed.oldest.days }) : "–"}
          cls={(d.owed.oldest?.days ?? 0) > 90 ? "text-status-red" : ""}
          hint={d.owed.oldest?.who ?? t("exp.nothingOwed")} />
        <Stat label={t("exp.reimbursed")} value={<Money value={d.claims.reimbursed_amount} lng={lng} />}
          cls="text-status-green" hint={t("exp.reimbursedHint", { n: d.claims.paid })} />
        <Stat label={t("exp.advancesOut")} value={<Money value={d.advances.unaccounted} lng={lng} />}
          cls={d.advances.unaccounted ? "text-status-amber" : ""} hint={t("exp.advancesOutHint", { n: d.advances.open })} />
        <Stat label={t("exp.reachesProject")} value={`${d.reach.with_project}/${d.reach.lines}`}
          cls={d.reach.with_project ? "" : "text-status-amber"} hint={t("exp.reachHint")} />
      </div>

      <Findings d={d} lng={lng} />

      <SectionCard title={t("exp.ageing")} aside={t("exp.ageingHint")}>
        <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4" data-expenses-ageing>
          {d.owed.buckets.map((b) => (
            <div key={b.bucket} data-expenses-bucket={b.bucket}>
              <div className={`font-heading text-xl font-semibold ${BUCKET_CLS[b.bucket] ?? ""}`}>
                <Money value={b.amount} lng={lng} />
              </div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">
                {t("exp.bucketDays", { bucket: b.bucket })} · {t("exp.claimsN", { n: b.count })}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("exp.queue")} aside={queue.data ? t("exp.queueN", { n: queue.data.count }) : ""}>
        {!queue.data ? <Skeleton className="m-4 h-24" /> : queue.data.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-expenses-queue-empty>{t("exp.queueEmpty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2">{t("exp.filed")}</th>
                <th className="px-2 py-2 text-right">{t("exp.claimed")}</th>
                <th className="px-2 py-2 text-right">{t("exp.sanctioned")}</th>
                <th className="px-2 py-2 text-right">{t("exp.waiting")}</th>
                <th className="px-4 py-2">{t("lab.decision")}</th>
              </tr>
            </thead>
            <tbody>
              {queue.data.rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-expenses-claim={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{r.company}{r.remark ? ` · ${r.remark}` : ""}</div>
                  </td>
                  <td className="px-2 py-2 tabular-nums">
                    {r.posting_date}
                    <div className={`text-[11px] ${r.age_days > 90 ? "text-status-red" : "text-brand-grey"}`}>
                      {t("exp.daysN", { n: r.age_days })}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-brand-grey"><Money value={r.total_claimed_amount} lng={lng} /></td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    <Money value={r.total_sanctioned_amount} lng={lng} />
                    {r.gap.cut > 0 && (
                      <div className="text-[11px] text-status-amber" data-expenses-cut={r.name}>
                        {t("exp.cutBy", { pct: r.gap.cut_pct })}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-semibold"><Money value={r.outstanding} lng={lng} /></td>
                  <td className="px-4 py-2">
                    {queue.data.can_pay ? (
                      <button onClick={() => pay.mutate({ claim: r.name })} disabled={pay.isPending}
                        data-expenses-pay={r.name}
                        className="rounded bg-status-green px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                        {t("exp.pay")}
                      </button>
                    ) : <span className="text-[11px] text-brand-grey">{t("exp.financeOnly")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {pay.isError && <ErrorBox error={pay.error} />}
    </div>
  );
}

function Findings({ d, lng }: { d: ExpenseBoard; lng: string }) {
  const { t } = useTranslation();
  const failing = d.state.filter((s) => !s.ok);
  if (failing.length === 0) {
    return (
      <div className="rounded border border-status-green/40 bg-green-50 px-4 py-2 text-sm text-status-green" data-expenses-findings>
        {t("exp.allClear")}
      </div>
    );
  }
  return (
    <SectionCard title={t("exp.findings")} aside={t("exp.findingsN", { n: failing.length, of: d.state.length })}>
      <ul>
        {failing.map((s) => (
          <li key={s.key} className="flex items-start gap-2 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
            data-expenses-finding={s.key}>
            <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
            <div>
              <div className="font-semibold">{t(`exp.finding.${s.key}.title`)}</div>
              <p className="mt-0.5 text-sm text-brand-grey">
                {t(`exp.finding.${s.key}.bad`, { ...s.facts, amount: money(s.facts.amount, lng) }) as string}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function money(value: unknown, lng: string) {
  const n = Number(value ?? 0);
  return `${new Intl.NumberFormat(lng === "fr" ? "fr-CD" : "en-GB").format(Math.round(n))} CDF`;
}
